import { formatToString, getFirstDayOfMonthForDate, getFirstDayOfWeekForDate, getFirstDayOfYearForDate, getInitialDayFromSettings, getLastDayFromSettings, getLastDayOfMonthForDate, getLastDayOfWeekForDate, getLastDayOfYearForDate } from "../../utils/DateUtils";
import { addDays, format, isBefore, isAfter } from 'date-fns';

/**
   * Processes graph data to create a continuous series from a start to an end date.
   * Fills in missing days with a value of 0.
   *
   * @param {object} graphConfiguration - Contains startDate and endDate strings ('YYYY-MM-DD').
   * @param {object} graphData - Contains the sparse data array.
   * @returns {{dates: string[], values: number[]}} - An object with complete dates and values.
   */
export function processContinuousGraphData(graphConfiguration, graphData) {
  const groupByTime = graphConfiguration.customGraphSettings.visualizationSettings.groupByTime;
  if (!graphData?.data) {
    return { dates: [], values: [] };
  }

  switch (groupByTime) {
    default:
    case "DAY":
      return processDataGroupByDay(graphConfiguration, graphData);
    case "WEEK":
      return processDataGroupByWeek(graphConfiguration, graphData);
    case "MONTH":
      return processDataGroupByMonth(graphConfiguration, graphData);
    case "YEAR":
      return processDataGroupByYear(graphConfiguration, graphData);
  }
}

/**
   * Processes graph data to create a continuous series from a start to an end date.
   * Fills in missing days with a value of 0.
   *
   * @param {object} graphConfiguration - Contains startDate and endDate strings ('YYYY-MM-DD').
   * @param {object} graphData - Contains the sparse data array.
   * @returns {{labels: string[], datasets: object[]}} - An object with complete dates and values.
   */
export function processGroupedGraphData(graphConfiguration, graphData, valueField = 'amount') {
  const groupByTime = graphConfiguration.customGraphSettings.visualizationSettings.groupByTime;
  const groupByCategory = graphConfiguration.customGraphSettings.visualizationSettings.groupByCategory;
  const groupByIncomeBankAccounts = graphConfiguration.customGraphSettings.visualizationSettings.groupByIncomeBankAccounts;
  const groupByIncomeSources = graphConfiguration.customGraphSettings.visualizationSettings.groupByIncomeSources;
  if (!graphData?.data) {
    return { dates: [], datasets: [] };
  }

  const dataMap = new Map();
  const categories = new Set(); // Could be categories, incomeBankAccounts or incomeSources

  graphData.data.forEach(dailyData => {
    const { date, categoryAmounts, incomeBankAccountAmounts, incomeSourceAmounts } = dailyData;
    if (!dataMap.has(date)) {
      dataMap.set(date, new Map());
    }
    if (groupByCategory) {
      categoryAmounts.forEach(categoryEntry => {
        categories.add(categoryEntry.category);
        dataMap.get(date).set(categoryEntry.category, categoryEntry[valueField]);
      });
    } else if (groupByIncomeBankAccounts) {
      incomeBankAccountAmounts.forEach(incomeBankAccountEntry => {
        categories.add(incomeBankAccountEntry.incomeBankAccount);
        dataMap.get(date).set(incomeBankAccountEntry.incomeBankAccount, incomeBankAccountEntry[valueField]);
      });
    } else if (groupByIncomeSources) {
      incomeSourceAmounts.forEach(incomeSourceEntry => {
        categories.add(incomeSourceEntry.incomeSource);
        dataMap.get(date).set(incomeSourceEntry.incomeSource, incomeSourceEntry[valueField]);
      });
    }
  });

  const labels = [];
  const uniqueCategories = Array.from(categories);
  let datasets = uniqueCategories.map(category => ({
    label: category,
    data: [],
  }));

  switch (groupByTime) {
    default:
    case "DAY":
      return processGroupedDataGroupByDay(graphConfiguration, graphData, labels, datasets, dataMap);
    case "WEEK":
      return processGroupedDataGroupByWeek(graphConfiguration, graphData, labels, datasets, dataMap);
    case "MONTH":
      return processGroupedDataGroupByMonth(graphConfiguration, graphData, labels, datasets, dataMap);
    case "YEAR":
      return processGroupedDataGroupByYear(graphConfiguration, graphData, labels, datasets, dataMap);
  }
}

/**
 * Processes grouped graph data using transaction counts instead of amounts.
 * Returns { labels, datasets } bucketed by groupByTime, with cumulative applied
 * when configured. Reuses processGroupedGraphData's time-bucketing and cumulative logic.
 */
export function processGroupedCountData(graphConfiguration, graphData) {
  return processGroupedGraphData(graphConfiguration, graphData, 'count');
}

export function computeAverage(values) {
  return values.length > 0
    ? values.reduce((sum, val) => sum + val, 0) / values.length
    : 0;
}

export function computeStandardDeviation(values) {
  const n = values.length;
  const mean = computeAverage(values);
  return Math.sqrt(values.map(x => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / n);
}

export function addAverageToAnnotations(annotations, graphConfiguration, graphData) {
  const { dates, values } = processContinuousGraphData(graphConfiguration, graphData);
  const averageValue = computeAverage(values);
  annotations.averageAnnotation = {
    type: 'line',
    borderColor: 'rgb(100, 149, 237)',
    borderDash: [6, 6],
    borderDashOffset: 0,
    borderWidth: 3,
    label: {
      display: true,
      backgroundColor: 'rgb(100, 149, 237)',
      content: 'Average: ' + averageValue.toFixed(2),
    },
    scaleID: 'y',
    value: averageValue
  };
}

export function addStandardDeviationToAnnotations(annotations, graphConfiguration, graphData) {
  const { dates, values } = processContinuousGraphData(graphConfiguration, graphData);
  const averageValue = computeAverage(values);
  const standardDeviationValue = computeStandardDeviation(values);
  annotations.upperStd = {
    type: 'line',
    borderColor: 'rgba(102, 102, 102, 0.5)',
    borderDash: [6, 6],
    borderDashOffset: 0,
    borderWidth: 3,
    label: {
      display: true,
      backgroundColor: 'rgba(102, 102, 102, 0.75)',
      color: 'white',
      content: (averageValue + standardDeviationValue).toFixed(2),
      position: 'start'
    },
    scaleID: 'y',
    value: averageValue + standardDeviationValue
  };
  annotations.lowerStd = {
    type: 'line',
    borderColor: 'rgba(102, 102, 102, 0.5)',
    borderDash: [6, 6],
    borderDashOffset: 0,
    borderWidth: 3,
    label: {
      display: true,
      backgroundColor: 'rgba(102, 102, 102, 0.75)',
      color: 'white',
      content: (averageValue - standardDeviationValue).toFixed(2),
      position: 'end'
    },
    scaleID: 'y',
    value: averageValue - standardDeviationValue
  };
}

export function getTimeUnitFromConfiguration(graphConfiguration) {
  const groupByTime = graphConfiguration.customGraphSettings.visualizationSettings.groupByTime;
  switch (groupByTime) {
    default:
    case "DAY":
      return 'day';
    case "WEEK":
      return 'week';
    case "MONTH":
      return 'month';
    case "YEAR":
      return 'year';
  }
}

export function getGraphTitle(graphConfiguration) {
  if (graphConfiguration.customTitle === null) {
    return getGraphTitleFromConfiguration(graphConfiguration);
  } else {
    return graphConfiguration.customTitle;
  }
}

export function getGraphTitleFromConfiguration(graphConfiguration) {
  let graphTitle = '';
  if (graphConfiguration.requestType === 'CUSTOM_GRAPH') {
    // Custom graph
    const source = graphConfiguration.customGraphSettings.dataSettings.source;
    const time = graphConfiguration.customGraphSettings.dataSettings.time;
    graphTitle += sourceToText(source) + ' ' + timeToText(time, graphConfiguration);
    const isCumulative = graphConfiguration.customGraphSettings.visualizationSettings.cumulative;
    if (isCumulative) {
      graphTitle += ' (cumulative)';
    }
  } else {
    // Burndown
    const burndownType = graphConfiguration.burndownSettings.type;
    const referenceType = graphConfiguration.burndownSettings.referenceSettings.type;
    const referenceCustomMonth = graphConfiguration.burndownSettings.referenceSettings.customMonth;
    const referenceCustomYear = graphConfiguration.burndownSettings.referenceSettings.customYear;
    const burndownTime = graphConfiguration.burndownSettings.dataSettings.time;
    const burndownCustomMonth = graphConfiguration.burndownSettings.dataSettings.customMonth;
    const burndownCustomYear = graphConfiguration.burndownSettings.dataSettings.customYear;
    if (burndownType === 'MONTHLY') {
      graphTitle += 'Burndown - ' + burndownTimeToText(burndownTime, burndownCustomMonth, null) + " data - " + referenceTypeToText(referenceType, referenceCustomMonth, null) + " reference";
    } else {
      graphTitle += 'Burndown - ' + burndownTimeToText(burndownTime, null, burndownCustomYear) + " data - " + referenceTypeToText(referenceType, null, referenceCustomYear) + " reference";
    }
  }
  return graphTitle;
}

// Returns a standalone human-readable label for a CUSTOM_GRAPH time setting.
// Intentionally separate from timeToText() which returns title-suffix phrasing.
function periodText(time, customStartDate, customEndDate) {
  switch (time) {
    default:
    case 'LAST_WEEK':
      return 'Last week';
    case 'LAST_MONTH':
      return 'Last month';
    case 'LAST_YEAR':
      return 'Last year';
    case 'CUSTOM':
      return (customStartDate || '') + ' to ' + (customEndDate || '');
    case 'NO_TIME':
      return 'All time';
  }
}

// Returns an array of { label, value } rows describing the chart's time boundaries.
export function getGraphPeriodInfo(graphConfiguration) {
  if (graphConfiguration.requestType === 'CUSTOM_GRAPH') {
    const ds = graphConfiguration.customGraphSettings.dataSettings;
    return [{ label: 'Period', value: periodText(ds.time, ds.customStartDate, ds.customEndDate) }];
  }
  // BURNDOWN — show both data and reference period
  const b = graphConfiguration.burndownSettings;
  return [
    { label: 'Data period', value: burndownTimeToText(b.dataSettings.time, b.dataSettings.customMonth, b.dataSettings.customYear) },
    { label: 'Reference', value: referenceTypeToText(b.referenceSettings.type, b.referenceSettings.customMonth, b.referenceSettings.customYear) },
  ];
}

// Returns an array of { label, values: string[] } rows describing active filters.
export function getGraphFilterInfo(graphConfiguration) {
  if (graphConfiguration.requestType === 'CUSTOM_GRAPH') {
    const fs = graphConfiguration.customGraphSettings.filterSettings;
    const source = graphConfiguration.customGraphSettings.dataSettings.source;
    const rows = [];
    if (source === 'EXPENSES' || source === 'SAVINGS') {
      rows.push({
        label: 'Categories',
        values: (fs.allExpenses || !fs.includedCategories?.length)
          ? ['All categories']
          : fs.includedCategories,
      });
    }
    if (source === 'INCOMES' || source === 'SAVINGS') {
      rows.push({
        label: 'Bank accounts',
        values: (fs.allIncomes || !fs.includedIncomeBankAccounts?.length)
          ? ['All bank accounts']
          : fs.includedIncomeBankAccounts,
      });
      rows.push({
        label: 'Income sources',
        values: (fs.allIncomes || !fs.includedIncomeSources?.length)
          ? ['All income sources']
          : fs.includedIncomeSources,
      });
    }
    return rows;
  }
  // BURNDOWN
  const fs = graphConfiguration.burndownSettings.filterSettings;
  return [{
    label: 'Categories',
    values: (fs.allExpenses || !fs.includedCategories?.length)
      ? ['All categories']
      : fs.includedCategories,
  }];
}

function sourceToText(source) {
  switch (source) {
    default:
    case 'EXPENSES':
      return 'Expenses';
    case 'INCOMES':
      return 'Incomes';
    case 'SAVINGS':
      return 'Savings';
  }
}

function referenceTypeToText(referenceType, customMonth, customYear) {
  switch (referenceType) {
    default:
    case 'TOTAL_AVERAGE':
      return "Total average";
    case 'LAST_YEAR_AVERAGE':
      return "Last year average";
    case 'BEST_MONTH':
      return "Best month";
    case 'CUSTOM_MONTH':
      return customMonth;
    case 'BEST_YEAR':
      return "Best year";
    case 'CUSTOM_YEAR':
      return String(customYear);
  }
}

function burndownTimeToText(time, customMonth, customYear) {
  switch (time) {
    default:
    case 'LAST_MONTH':
      return "Last month";
    case 'CUSTOM_MONTH':
      return customMonth;
    case 'LAST_YEAR':
      return "Last year";
    case 'CUSTOM_YEAR':
      return String(customYear);
  }
}

function timeToText(time, graphConfiguration) {
  switch (time) {
    default:
    case 'LAST_WEEK':
      return 'in the last week';
    case 'LAST_MONTH':
      return 'in the last month';
    case 'LAST_YEAR':
      return 'in the last year';
    case 'CUSTOM':
      const customStartDate = graphConfiguration.customGraphSettings.dataSettings.customStartDate;
      const customEndDate = graphConfiguration.customGraphSettings.dataSettings.customEndDate;
      return '(' + customStartDate + ' to ' + customEndDate + ')';
    case 'NO_TIME':
      return '';
  }
}

function processDataGroupByDay(graphConfiguration, graphData) {
  const dates = [];
  const values = [];
  const dataMap = new Map(graphData.data.map(item => [item.date, item.totalAmount]));

  const isCumulative = graphConfiguration.customGraphSettings.visualizationSettings.cumulative;
  var cumulativeValue = 0;

  const firstDate = getInitialDayFromSettings(graphConfiguration, graphData);
  const lastDate = getLastDayFromSettings(graphConfiguration, graphData);
  let currentDate = firstDate;

  while (currentDate <= lastDate) {
    const dateString = formatToString(currentDate);
    dates.push(dateString);
    const dailyTotal = dataMap.get(dateString) || 0;
    cumulativeValue += dailyTotal;

    if (isCumulative) {
      values.push(cumulativeValue);
    } else {
      values.push(dailyTotal);
    }
    currentDate = addDays(currentDate, 1);
  }

  return { dates, values };
}

function processDataGroupByWeek(graphConfiguration, graphData) {
  const dates = [];
  const values = [];
  const dataMap = new Map(graphData.data.map(item => [item.date, item.totalAmount]));

  const isCumulative = graphConfiguration.customGraphSettings.visualizationSettings.cumulative;
  var cumulativeValue = 0;

  const firstDate = getInitialDayFromSettings(graphConfiguration, graphData);
  const lastDate = getLastDayFromSettings(graphConfiguration, graphData);
  let currentDate = firstDate;

  while (currentDate <= lastDate) {
    const weekStart = getFirstDayOfWeekForDate(currentDate);
    const weekEnd = getLastDayOfWeekForDate(currentDate);

    let weeklyTotal = 0;
    let dayInWeek = new Date(weekStart);
    // Iterate through each day within this week to sum up values
    while (dayInWeek <= weekEnd) {
      const isAfterStartDate = dayInWeek >= firstDate;
      const isBeforeEndDate = dayInWeek <= lastDate;

      if (isAfterStartDate && isBeforeEndDate) {
        const dateString = formatToString(dayInWeek);
        weeklyTotal += dataMap.get(dateString) || 0;
      }
      dayInWeek = addDays(dayInWeek, 1);
    }

    dates.push(formatToString(weekStart));
    cumulativeValue += weeklyTotal;

    if (isCumulative) {
      values.push(cumulativeValue);
    } else {
      values.push(weeklyTotal);
    }
    // Jump to start of the next week
    currentDate = addDays(weekEnd, 1);
  }

  return { dates, values };
}

function processDataGroupByMonth(graphConfiguration, graphData) {
  const dates = [];
  const values = [];
  const dataMap = new Map(graphData.data.map(item => [item.date, item.totalAmount]));

  const isCumulative = graphConfiguration.customGraphSettings.visualizationSettings.cumulative;
  var cumulativeValue = 0;

  const firstDate = getInitialDayFromSettings(graphConfiguration, graphData);
  const lastDate = getLastDayFromSettings(graphConfiguration, graphData);
  let currentDate = firstDate;
  while (currentDate <= lastDate) {
    const monthStart = getFirstDayOfMonthForDate(currentDate);
    const monthEnd = getLastDayOfMonthForDate(currentDate);

    let monthlyTotal = 0;
    let dayInMonth = new Date(monthStart);
    // Iterate through each day within this month to sum up values
    while (dayInMonth <= monthEnd) {
      const isAfterStartDate = dayInMonth >= firstDate;
      const isBeforeEndDate = dayInMonth <= lastDate;

      if (isAfterStartDate && isBeforeEndDate) {
        const dateString = formatToString(dayInMonth);
        monthlyTotal += dataMap.get(dateString) || 0;
      }
      dayInMonth = addDays(dayInMonth, 1);
    }

    dates.push(formatToString(monthStart));
    cumulativeValue += monthlyTotal;

    if (isCumulative) {
      values.push(cumulativeValue);
    } else {
      values.push(monthlyTotal);
    }
    // Jump to start of the next month
    currentDate = addDays(monthEnd, 1);
  }

  return { dates, values };
}

function processDataGroupByYear(graphConfiguration, graphData) {
  const dates = [];
  const values = [];
  const dataMap = new Map(graphData.data.map(item => [item.date, item.totalAmount]));

  const isCumulative = graphConfiguration.customGraphSettings.visualizationSettings.cumulative;
  var cumulativeValue = 0;

  const firstDate = getInitialDayFromSettings(graphConfiguration, graphData);
  const lastDate = getLastDayFromSettings(graphConfiguration, graphData);
  let currentDate = firstDate;

  while (currentDate <= lastDate) {
    const yearStart = getFirstDayOfYearForDate(currentDate);
    const yearEnd = getLastDayOfYearForDate(currentDate);

    let yearlyTotal = 0;
    let dayInYear = new Date(yearStart);
    while (dayInYear <= yearEnd) {
      const isAfterStartDate = dayInYear >= firstDate;
      const isBeforeEndDate = dayInYear <= lastDate;

      if (isAfterStartDate && isBeforeEndDate) {
        const dateString = formatToString(dayInYear);
        yearlyTotal += dataMap.get(dateString) || 0;
      }
      dayInYear = addDays(dayInYear, 1);
    }

    dates.push(formatToString(yearStart));
    cumulativeValue += yearlyTotal;

    if (isCumulative) {
      values.push(cumulativeValue);
    } else {
      values.push(yearlyTotal);
    }
    // Jump to start of the next year
    currentDate = addDays(yearEnd, 1);
  }

  return { dates, values };
}

function processGroupedDataGroupByDay(graphConfiguration, graphData, labels, datasets, dataMap) {
  const isCumulative = graphConfiguration.customGraphSettings.visualizationSettings.cumulative;
  const cumulativesPerCategory = new Map();
  const firstDate = getInitialDayFromSettings(graphConfiguration, graphData);
  const lastDate = getLastDayFromSettings(graphConfiguration, graphData);
  let currentDate = firstDate;
  while (currentDate <= lastDate) {
    labels.push(formatToString(currentDate));
    currentDate = addDays(currentDate, 1);
  }

  labels.forEach(date => {
    datasets.forEach(dataset => {
      const dailyCategoryMap = dataMap.get(date);
      let valueToPush;
      const dailyAmount = dailyCategoryMap ? dailyCategoryMap.get(dataset.label) || 0 : 0;

      if (isCumulative) {
        const previousCumulative = cumulativesPerCategory.get(dataset.label) || 0;
        const newCumulative = previousCumulative + dailyAmount;
        cumulativesPerCategory.set(dataset.label, newCumulative);
        valueToPush = newCumulative;
      } else {
        valueToPush = dailyAmount;
      }
      dataset.data.push(valueToPush);
    });
  });

  datasets = applyColorToDatasets(datasets, graphConfiguration);
  return { labels, datasets };
}

function processGroupedDataGroupByWeek(graphConfiguration, graphData, labels, datasets, dataMap) {
  const isCumulative = graphConfiguration.customGraphSettings.visualizationSettings.cumulative;
  const cumulativesPerCategory = new Map();
  const firstDate = getInitialDayFromSettings(graphConfiguration, graphData);
  const lastDate = getLastDayFromSettings(graphConfiguration, graphData);
  let currentDate = firstDate;
  while (currentDate <= lastDate) {
    const weekStart = getFirstDayOfWeekForDate(currentDate);
    const weekEnd = getLastDayOfWeekForDate(currentDate);

    labels.push(formatToString(weekStart));

    const weeklyTotals = new Map();
    let dayInWeek = new Date(weekStart);
    while (dayInWeek <= weekEnd) {
      const isAfterStartDate = dayInWeek >= firstDate;
      const isBeforeEndDate = dayInWeek <= lastDate;

      if (isAfterStartDate && isBeforeEndDate) {
        const dateString = formatToString(dayInWeek);
        const dailyCategoryMap = dataMap.get(dateString);
        if (dailyCategoryMap) {
          dailyCategoryMap.forEach((amount, category) => {
            const currentTotal = weeklyTotals.get(category) || 0;
            weeklyTotals.set(category, currentTotal + amount);
          });
        }
      }
      dayInWeek = addDays(dayInWeek, 1);
    }

    datasets.forEach(dataset => {
      const categoryLabel = dataset.label;
      const weeklyAmount = weeklyTotals.get(categoryLabel) || 0;

      let valueToPush;
      if (isCumulative) {
        const previousCumulative = cumulativesPerCategory.get(categoryLabel) || 0;
        const newCumulative = previousCumulative + weeklyAmount;
        cumulativesPerCategory.set(categoryLabel, newCumulative);
        valueToPush = newCumulative;
      } else {
        valueToPush = weeklyAmount;
      }
      dataset.data.push(valueToPush);
    });
    // Jump to start of the next week
    currentDate = addDays(weekEnd, 1);
  }

  datasets = applyColorToDatasets(datasets, graphConfiguration);
  return { labels, datasets };
}

function processGroupedDataGroupByMonth(graphConfiguration, graphData, labels, datasets, dataMap) {
  const isCumulative = graphConfiguration.customGraphSettings.visualizationSettings.cumulative;
  const cumulativesPerCategory = new Map();
  const firstDate = getInitialDayFromSettings(graphConfiguration, graphData);
  const lastDate = getLastDayFromSettings(graphConfiguration, graphData);
  let currentDate = firstDate;
  while (currentDate <= lastDate) {
    const monthStart = getFirstDayOfMonthForDate(currentDate);
    const monthEnd = getLastDayOfMonthForDate(currentDate);

    labels.push(formatToString(monthStart));

    const monthlyTotals = new Map();
    let dayInMonth = new Date(monthStart);
    while (dayInMonth <= monthEnd) {
      const isAfterStartDate = dayInMonth >= firstDate;
      const isBeforeEndDate = dayInMonth <= lastDate;

      if (isAfterStartDate && isBeforeEndDate) {
        const dateString = formatToString(dayInMonth);
        const dailyCategoryMap = dataMap.get(dateString);
        if (dailyCategoryMap) {
          dailyCategoryMap.forEach((amount, category) => {
            const currentTotal = monthlyTotals.get(category) || 0;
            monthlyTotals.set(category, currentTotal + amount);
          });
        }
      }
      dayInMonth = addDays(dayInMonth, 1);
    }

    datasets.forEach(dataset => {
      const categoryLabel = dataset.label;
      const monthlyAmount = monthlyTotals.get(categoryLabel) || 0;

      let valueToPush;
      if (isCumulative) {
        const previousCumulative = cumulativesPerCategory.get(categoryLabel) || 0;
        const newCumulative = previousCumulative + monthlyAmount;
        cumulativesPerCategory.set(categoryLabel, newCumulative);
        valueToPush = newCumulative;
      } else {
        valueToPush = monthlyAmount;
      }
      dataset.data.push(valueToPush);
    });
    // Jump to start of the next month
    currentDate = addDays(monthEnd, 1);
  }

  datasets = applyColorToDatasets(datasets, graphConfiguration);
  return { labels, datasets };
}

function processGroupedDataGroupByYear(graphConfiguration, graphData, labels, datasets, dataMap) {
  const isCumulative = graphConfiguration.customGraphSettings.visualizationSettings.cumulative;
  const cumulativesPerCategory = new Map();
  const firstDate = getInitialDayFromSettings(graphConfiguration, graphData);
  const lastDate = getLastDayFromSettings(graphConfiguration, graphData);
  let currentDate = firstDate;
  while (currentDate <= lastDate) {
    const yearStart = getFirstDayOfYearForDate(currentDate);
    const yearEnd = getLastDayOfYearForDate(currentDate);

    labels.push(formatToString(yearStart));

    const yearlyTotals = new Map();
    let dayInYear = new Date(yearStart);
    while (dayInYear <= yearEnd) {
      const isAfterStartDate = dayInYear >= firstDate;
      const isBeforeEndDate = dayInYear <= lastDate;

      if (isAfterStartDate && isBeforeEndDate) {
        const dateString = formatToString(dayInYear);
        const dailyCategoryMap = dataMap.get(dateString);
        if (dailyCategoryMap) {
          dailyCategoryMap.forEach((amount, category) => {
            const currentTotal = yearlyTotals.get(category) || 0;
            yearlyTotals.set(category, currentTotal + amount);
          });
        }
      }
      dayInYear = addDays(dayInYear, 1);
    }

    datasets.forEach(dataset => {
      const categoryLabel = dataset.label;
      const yearlyAmount = yearlyTotals.get(categoryLabel) || 0;

      let valueToPush;
      if (isCumulative) {
        const previousCumulative = cumulativesPerCategory.get(categoryLabel) || 0;
        const newCumulative = previousCumulative + yearlyAmount;
        cumulativesPerCategory.set(categoryLabel, newCumulative);
        valueToPush = newCumulative;
      } else {
        valueToPush = yearlyAmount;
      }
      dataset.data.push(valueToPush);
    });
    // Jump to start of the next month
    currentDate = addDays(yearEnd, 1);
  }

  datasets = applyColorToDatasets(datasets, graphConfiguration);
  return { labels, datasets };
}

/**
 * Calculates heatmap configuration data from graphConfiguration and graphData.
 * 
 * @param {object} graphConfiguration - Contains date range settings.
 * @param {object} graphData - Contains the data array with date and totalAmount.
 * @returns {{startDate: Date, numberOfMonths: number, maxValue: number}}
 */
export function calculateHeatmapData(graphConfiguration, graphData) {
  if (!graphData?.data || graphData.data.length === 0) {
    return { startDate: new Date(), numberOfMonths: 1, maxValue: 100 };
  }

  const firstDate = getInitialDayFromSettings(graphConfiguration, graphData);
  const lastDate = getLastDayFromSettings(graphConfiguration, graphData);

  // Calculate number of months between dates
  const numberOfMonths = (lastDate.getFullYear() - firstDate.getFullYear()) * 12
    + (lastDate.getMonth() - firstDate.getMonth()) + 1;

  // Find max value for color scale
  const maxValue = Math.max(...graphData.data.map(d => d.totalAmount), 1);

  return { startDate: firstDate, numberOfMonths, maxValue };
}

export const COLOR_PALETTE = [
  'rgb(54, 162, 235)',      // Blue
  'rgb(255, 99, 132)',      // Red
  'rgb(75, 192, 192)',      // Teal
  'rgb(255, 205, 86)',      // Yellow
  'rgb(153, 102, 255)',     // Purple
  'rgb(255, 159, 64)',      // Orange
  'rgb(201, 203, 207)',     // Grey
  'rgb(230, 100, 100)',     // Light Red
  'rgb(100, 230, 100)',     // Light Green
  'rgb(100, 100, 230)',     // Light Blue
];

function applyColorToDatasets(datasets, graphConfiguration) {
  const type = graphConfiguration.customGraphSettings.visualizationSettings.type;
  const colorPalette = COLOR_PALETTE;
  datasets.forEach((dataset, index) => {
    const solidColor = colorPalette[index % colorPalette.length];
    const transparentColor = solidColor.replace('rgb', 'rgba').replace(')', ', 0.3)');

    dataset.backgroundColor = transparentColor;
    dataset.borderColor = solidColor;
    if (type === 'LINE') {
      dataset.borderWidth = 2;
    } else {
      dataset.borderWidth = 1.5;
    }

    dataset.tension = 0.0;
    dataset.fill = 'stack';
    dataset.pointRadius = 0;
    dataset.pointHitRadius = 10;
  });
  return datasets;
}

function computeMedian(sortedValues) {
  const n = sortedValues.length;
  if (n === 0) return 0;
  const mid = Math.floor(n / 2);
  return n % 2 !== 0 ? sortedValues[mid] : (sortedValues[mid - 1] + sortedValues[mid]) / 2;
}

/**
 * Processes raw graph data for the Frequency (Bubble) chart.
 * Returns one aggregated entry per category / bank account / income source:
 *   { label, count, totalAmount, avgAmount }
 *
 * Each entry maps to one bubble:  x = count,  y = totalAmount,  r = f(avgAmount)
 *
 * Non-grouped fallback: if no candidate array carries a `count` field, produces a
 * single entry for the whole dataset with count = 0.
 */
export function processFrequencyGraphData(graphConfiguration, graphData) {
  if (!graphData?.data || graphData.data.length === 0) return [];

  const vis = graphConfiguration.customGraphSettings.visualizationSettings;

  // Respect the same grouping flags as processGroupedGraphData
  const CANDIDATES = [
    { arrayKey: 'categoryAmounts', labelKey: 'category', active: vis.groupByCategory },
    { arrayKey: 'incomeBankAccountAmounts', labelKey: 'incomeBankAccount', active: vis.groupByIncomeBankAccounts },
    { arrayKey: 'incomeSourceAmounts', labelKey: 'incomeSource', active: vis.groupByIncomeSources },
  ];

  // Use the config-selected array; fall back to data-driven detection if none is flagged
  const match =
    CANDIDATES.find(c => c.active) ||
    CANDIDATES.find(({ arrayKey }) =>
      graphData.data.some(d => (d[arrayKey] || []).some(entry => entry.count != null))
    );

  if (match) {
    const { arrayKey, labelKey } = match;
    const groupMap = new Map();

    graphData.data.forEach(d => {
      (d[arrayKey] || []).forEach(entry => {
        const label = entry[labelKey];
        const count = entry.count ?? 0;
        const amount = entry.amount ?? 0;
        if (!groupMap.has(label)) groupMap.set(label, { count: 0, totalAmount: 0, txAmounts: [] });
        const acc = groupMap.get(label);
        acc.count += count;
        acc.totalAmount += amount;
        if (count > 0) acc.txAmounts.push(amount / count);
      });
    });

    return Array.from(groupMap.entries())
      .map(([label, { count, totalAmount, txAmounts }]) => {
        const sorted = [...txAmounts].sort((a, b) => a - b);
        return {
          label,
          count,
          totalAmount,
          avgAmount: count > 0 ? totalAmount / count : 0,
          medianAmount: computeMedian(sorted),
        };
      })
      .sort((a, b) => b.totalAmount - a.totalAmount);
  }

  // Non-grouped fallback — one bubble for the whole dataset, no count available
  const totalAmount = graphData.data.reduce((sum, d) => sum + (d.totalAmount ?? 0), 0);
  const graphTitle = getGraphTitleFromConfiguration(graphConfiguration);
  return [{ label: graphTitle, count: 0, totalAmount, avgAmount: 0, medianAmount: 0 }];
}